import { MaskOnIcon, Pencil2Icon } from '@radix-ui/react-icons'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useGeckoAnalytics } from 'features/analytics'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { KestrelViewerTool } from './KestrelViewerTool'

const SegmentationModesContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SubModesContainer = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  left: 100%;
  top: 0;
  display: flex;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transition: opacity 0.2s ease-in-out;
`

const TextSegmentationButton = styled.button<{ $selected: boolean }>`
  cursor: pointer !important;
  background-color: ${({ theme, $selected }) =>
    $selected
      ? theme.forecast.background4
      : theme.forecast.background1} !important;
  opacity: 0.8 !important;
  padding: 8px !important;
  height: 28px !important;
  width: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: none;
  all: unset;

  &:hover {
    opacity: ${({ $selected }) => ($selected ? 0.8 : 1)} !important;
    background-color: ${({ theme, $selected }) =>
      $selected
        ? theme.forecast.background4
        : theme.forecast.background3} !important;
  }
`

export const SegmentationModes: React.FC = () => {
  const {
    selectedMode,
    setSelectedMode,
    sendCommand,
    textSegmentationPopoverVisible,
    setTextSegmentationPopoverVisible,
  } = useKestrelViewerContext()
  const { trackButtonClicked } = useGeckoAnalytics()
  const { kestrelModel } = useKestrel()
  const [showSubModes, setShowSubModes] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.relatedTarget as Node)
      ) {
        setShowSubModes(false)
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      if (container) {
        container.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [])

  const handleTextSegmentationClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    // Set mode to segmentation if not already
    if (selectedMode !== 'segmentation') {
      setSelectedMode('segmentation')
      sendCommand({ command: KestrelCommands.SET_MODE, mode: 'segmentation' })
      sendCommand({
        command: KestrelCommands.SET_POINTER_MODE,
        createAnnotationOnClick: true,
      })

      trackButtonClicked({
        button_name: `kestrel_set_mode`,
        kestrel_model_id: kestrelModel?.data.kestrel_model_id,
        mode: 'segmentation',
      })

      // First click: set mode and show popover
      setTextSegmentationPopoverVisible(true)
    } else {
      // Already in segmentation mode: toggle popover
      setTextSegmentationPopoverVisible(!textSegmentationPopoverVisible)
    }
  }

  return (
    <SegmentationModesContainer
      ref={containerRef}
      onMouseEnter={() => setShowSubModes(true)}>
      <KestrelViewerTool
        tooltipLabel={'Segmentation'}
        tooltipSide='top'
        modeId='segmentation'
        selectedMode={selectedMode}>
        <MaskOnIcon width={25} height={25} />
      </KestrelViewerTool>

      <SubModesContainer $isVisible={showSubModes}>
        <TooltipWrapper side='top' offset={5} label='Text segmentation'>
          <TextSegmentationButton
            $selected={textSegmentationPopoverVisible}
            onClick={handleTextSegmentationClick}>
            <Pencil2Icon width={25} height={25} />
          </TextSegmentationButton>
        </TooltipWrapper>
      </SubModesContainer>
    </SegmentationModesContainer>
  )
}
